import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DraftRiskTermsDocument from "./DraftRiskTermsDocument";

function shouldLogTerms() {
  try {
    if (typeof window === "undefined") return false;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("debugTerms") === "1") return true;
    if (window.localStorage?.getItem("debugTerms") === "1") return true;
  } catch (_) {
    // ignore
  }
  return false;
}

export default function TermsDialog({
  open,
  onClose,
  onAccept,
  language,
  buyerName,
  clientName,
  projectTitle,
  taxId,
}) {
  const ptLang = "pt-BR";
  const enLang = "en-US";

  const defaultLang = useMemo(() => {
    const raw = String(language || "").toLowerCase();
    return raw.startsWith("pt") ? ptLang : enLang;
  }, [language]);

  const [activeLang, setActiveLang] = useState(defaultLang);

  useEffect(() => {
    if (open) setActiveLang(defaultLang);
  }, [open, defaultLang]);

  useEffect(() => {
    if (!open) return;
    if (!shouldLogTerms()) return;
    console.info("[terms][TermsDialog] opened", {
      language,
      defaultLang,
      activeLang,
      buyerName: String(buyerName || "").trim(),
      clientName: String(clientName || "").trim(),
      projectTitle: projectTitle || "",
      hasTaxId: !!taxId,
    });
  }, [open, language, defaultLang, activeLang, buyerName, clientName, projectTitle, taxId]);

  const title = (activeLang || "").toLowerCase().startsWith("pt")
    ? "Termos e Condições"
    : "Terms & Conditions";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#fff" }}>
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={activeLang}
            onChange={(_, v) => setActiveLang(v)}
            variant="standard"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value={ptLang} label="Português (PT-BR)" />
            <Tab value={enLang} label="English (EN-US)" />
          </Tabs>
        </Box>

        {(clientName || projectTitle) && (
          <Box sx={{ mb: 2 }}>
            {clientName && (
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block" }}>
                {(activeLang || "").toLowerCase().startsWith("pt") ? "Cliente" : "Client"}: {clientName}
              </Typography>
            )}
            {projectTitle && (
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block" }}>
                {(activeLang || "").toLowerCase().startsWith("pt") ? "Projeto" : "Project"}: {projectTitle}
              </Typography>
            )}
          </Box>
        )}

        <DraftRiskTermsDocument
          language={activeLang}
          buyerName={buyerName}
          clientName={clientName}
          projectTitle={projectTitle}
          taxId={taxId}
          titleVariant="h6"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 999, textTransform: "none" }}>
          {(activeLang || "").toLowerCase().startsWith("pt") ? "Voltar" : "Back"}
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
        >
          {(activeLang || "").toLowerCase().startsWith("pt") ? "Aceitar" : "Accept"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
